import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Router} from '@angular/router';
import {FormBuilder, FormControl, FormGroup, NgForm, Validators} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {HttpErrorResponse} from '@angular/common/http';
import {of, ReplaySubject} from 'rxjs';
import {concatMap, map, takeUntil} from 'rxjs/operators';
import {AngularEditorConfig} from '@kolkov/angular-editor';
import {DobryAgentBlogArticle} from '../../../../shared/interface/dobry-agent-blog-article';
import {DobryAgentBlogService} from '../../../../shared/services/dobry-agent-blog.service';
import {Uuid} from '../../../../shared/interface/response/uuid';
import {environment} from "../../../../../environments/environment";

@Component({
  selector: 'app-da-article-add',
  templateUrl: './da-article-add.component.html',
  styleUrls: ['./da-article-add.component.scss']
})
export class DaArticleAddComponent implements OnInit, OnChanges, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() blogArticle?: DobryAgentBlogArticle;
  @Input() mod = false;

  @Output() closeModification = new EventEmitter<void>();

  uuid?: string;
  isEditMode = false;
  loading = false;
  saving = false;
  selectedImageFile: File | null = null;
  selectedImageFileName = '';
  selectedImagePreview: string | null = null;

  articleReactiveForm: FormGroup;
  authorControl: FormControl = new FormControl(null);
  blogCategoryControl: FormControl = new FormControl(null);
  insuranceCategoryControl: FormControl = new FormControl(null);
  relatedFacilityControl: FormControl = new FormControl(null);
  relatedBlogArticleControl = new FormControl([]);
  blogTagsControl = new FormControl([]);

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '260px',
    minHeight: '180px',
    maxHeight: '500px',
    width: '100%',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Wpisz treść artykułu...',
    defaultParagraphSeparator: 'p',
    defaultFontName: 'Arial',
    sanitize: false,
    toolbarHiddenButtons: [
      [
        'strikeThrough',
        'subscript',
        'superscript',
        'justifyRight',
        'justifyFull',
        'indent',
        'outdent',
        'heading',
        'fontName',
        'fontSize',
        'textColor',
        'backgroundColor',
        'customClasses',
        'insertImage',
        'insertVideo',
      ],
      []
    ]
  };

  model: any = {
    name: '',
    position: undefined,

    blogCategory: '',
    author: '',
    coordinator: '',

    shortDescription: '',
    publicationDate: '',

    altImage: '',
    blogTag: '',
    insuranceCategory: '',
    relatedBlogArticle: '',
    relatedFacility: '',

    seoSlug: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',

    active: true,
  };

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private blogService: DobryAgentBlogService,
    private fb: FormBuilder,
  ) {
    this.articleReactiveForm = this.fb.group({
      description: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    if (this.mod && this.blogArticle) {
      this.setModel(this.blogArticle);
      return;
    }

    this.isEditMode = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.blogArticle && this.blogArticle) {
      this.setModel(this.blogArticle);
    }
  }

  ngOnDestroy(): void {
    this.clearSelectedImagePreview();

    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getAuthorControl(): FormControl {
    return this.authorControl;
  }
  getBlogCategoryControl(): FormControl {
    return this.blogCategoryControl;
  }
  getInsuranceCategoryControl(): FormControl {
    return this.insuranceCategoryControl;
  }

  getRelatedFacilityControl(): FormControl {
    return this.relatedFacilityControl;
  }

  getRelatedBlogArticleControl(): FormControl {
    return this.relatedBlogArticleControl;
  }

  getBlogTagsControl(): FormControl {
    return this.blogTagsControl;
  }

  save(form: NgForm, addNext = false): void {
    if (form.invalid || this.articleReactiveForm.invalid || this.saving) {
      form.control.markAllAsTouched();
      this.articleReactiveForm.markAllAsTouched();
      return;
    }

    this.saving = true;

    const formData = {
      name: this.model.name,
      position: this.model.position,

      blogCategory: this.emptyToNull(this.blogCategoryControl.value),
      author: this.emptyToNull(this.authorControl.value),
      coordinator: this.emptyToNull(this.model.coordinator),

      description: this.articleReactiveForm.get('description')?.value,
      shortDescription: this.model.shortDescription,

      publicationDate: this.model.publicationDate,

      altImage: this.model.altImage,

      blogTags: this.blogTagsControl.value ?? [],
      insuranceCategory: this.emptyToNull(
        this.insuranceCategoryControl.value
      ),

      relatedArticles: this.relatedBlogArticleControl.value ?? [],

      relatedFacility: this.emptyToNull(
        this.relatedFacilityControl.value
      ),

      seoSlug: this.model.seoSlug,
      seoTitle: this.model.seoTitle,
      seoDescription: this.model.seoDescription,
      seoKeywords: this.model.seoKeywords,

      active: !!this.model.active,
    };

    this.blogService.send(formData, this.uuid)
      .pipe(
        takeUntil(this.destroyed$),
        concatMap((result: Uuid) => {
          if (this.selectedImageFile && result?.uuid) {
            return this.blogService
              .uploadImage(this.selectedImageFile, result.uuid)
              .pipe(map(() => result));
          }

          return of(result);
        })
      )
      .subscribe({
        next: (result: Uuid) => {
          this.saving = false;

          this.snackBar.open(
            this.isEditMode ? 'Artykuł został zapisany.' : 'Artykuł został dodany.',
            'Zamknij',
            {duration: 3000}
          );

          this.selectedImageFile = null;
          this.selectedImageFileName = '';
          this.clearSelectedImagePreview();

          if (this.mod) {
            this.closeModification.emit();
            return;
          }

          if (addNext) {
            this.resetForm(form);
            return;
          }

          if (result?.uuid) {
            this.router.navigate(['/blog-dobry-agent/artykuly/szczegoly', result.uuid]);
            return;
          }

          this.router.navigate(['/blog-dobry-agent/artykuly']);
        },
        error: (error: HttpErrorResponse) => {
          this.saving = false;
          this.handleBackendError(error, form);
        }
      });
  }
  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || !input.files.length) {
      this.selectedImageFile = null;
      this.selectedImageFileName = '';
      this.clearSelectedImagePreview();
      return;
    }

    const file = input.files[0];

    if (!file.type.startsWith('image/')) {
      this.selectedImageFile = null;
      this.selectedImageFileName = '';
      this.clearSelectedImagePreview();

      this.snackBar.open(
        'Wybrany plik nie jest obrazkiem.',
        'Zamknij',
        {duration: 4000}
      );

      input.value = '';
      return;
    }

    this.selectedImageFile = file;
    this.selectedImageFileName = file.name;

    this.setSelectedImagePreview(file);
  }

  clearSelectedImage(imageInput: HTMLInputElement): void {
    this.selectedImageFile = null;
    this.selectedImageFileName = '';
    this.clearSelectedImagePreview();
    imageInput.value = '';
  }

  private setSelectedImagePreview(file: File): void {
    this.clearSelectedImagePreview();

    const reader = new FileReader();

    reader.onload = () => {
      this.selectedImagePreview = reader.result?.toString() ?? null;
    };

    reader.readAsDataURL(file);
  }

  private clearSelectedImagePreview(): void {
    if (this.selectedImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(this.selectedImagePreview);
    }

    this.selectedImagePreview = null;
  }
  cancel(): void {
    if (this.mod) {
      this.closeModification.emit();
      return;
    }

    this.router.navigate(['/blog-dobry-agent/artykuly']);
  }

  clearBackendError(form: NgForm, fieldName: string): void {
    const control = form.controls[fieldName];

    if (!control?.errors?.backend) {
      return;
    }

    const errors = {...control.errors};
    delete errors.backend;

    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  clearDescriptionBackendError(): void {
    const control = this.articleReactiveForm.get('description');

    if (!control?.errors?.backend) {
      return;
    }

    const errors = {...control.errors};
    delete errors.backend;

    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  private handleBackendError(error: HttpErrorResponse, form: NgForm): void {
    const backendError = error.error?.error;
    const errorList = backendError?.list;

    if (errorList) {
      Object.keys(errorList).forEach((fieldName: string) => {
        const fieldErrors = errorList[fieldName];

        if (fieldName === 'description' && fieldErrors?.length) {
          this.articleReactiveForm.get('description')?.setErrors({
            backend: fieldErrors[0]
          });

          this.articleReactiveForm.get('description')?.markAsTouched();
          return;
        }

        if (form.controls[fieldName] && fieldErrors?.length) {
          form.controls[fieldName].setErrors({
            backend: fieldErrors[0]
          });

          form.controls[fieldName].markAsTouched();
        }
      });

      this.snackBar.open(
        backendError?.text || 'Formularz zawiera błędy.',
        'Zamknij',
        {duration: 4000}
      );

      return;
    }

    this.snackBar.open(
      backendError?.text || 'Nie udało się zapisać artykułu. Sprawdź formularz i spróbuj ponownie.',
      'Zamknij',
      {duration: 4000}
    );
  }

  private setModel(article: DobryAgentBlogArticle): void {
    this.uuid = article.uuid;
    this.isEditMode = true;

    this.model = {
      uuid: article.uuid,
      name: article.name ?? '',
      position: article.position ?? undefined,

      coordinator: article.coordinator?.uuid ?? '',

      shortDescription: article.shortDescription ?? '',
      publicationDate: this.toDateTimeLocal(article.publicationDateIso ?? article.publicationDate),

      altImage: article.altImage ?? '',

      seoSlug: article.seoSlug ?? '',
      seoTitle: article.seoTitle ?? '',
      seoDescription: article.seoDescription ?? '',
      seoKeywords: article.seoKeywords ?? '',

      active: article.active ?? false,
    };

    this.articleReactiveForm.patchValue({
      description: article.description ?? '',
    });

    this.authorControl.setValue(
      article.author?.uuid ?? null,
      {emitEvent: false}
    );
    this.blogCategoryControl.setValue(
      article.blogCategory?.uuid ?? null,
      {emitEvent: false}
    );

    this.insuranceCategoryControl.setValue(
      article.insuranceCategory?.uuid ?? null,
      {emitEvent: false}
    );
    this.relatedFacilityControl.setValue(
      article.relatedFacility?.uuid ?? null,
      {emitEvent: false}
    );

    this.relatedBlogArticleControl.setValue(
      article.relatedArticles?.map(
        relatedArticle => relatedArticle.uuid
      ) ?? [],
      {emitEvent: false}
    );

    this.blogTagsControl.setValue(
      article.blogTags?.map(tag => tag.uuid) ?? [],
      {emitEvent: false}
    );
  }

  private resetForm(form: NgForm): void {
    this.uuid = undefined;
    this.isEditMode = false;

    this.model = {
      name: '',
      position: undefined,

      blogCategory: '',
      author: '',
      coordinator: '',

      shortDescription: '',
      publicationDate: null,

      altImage: '',
      blogTag: '',
      insuranceCategory: '',
      relatedBlogArticle: '',
      relatedFacility: '',

      seoSlug: '',
      seoTitle: '',
      seoDescription: '',
      seoKeywords: '',

      active: true,
    };

    this.selectedImageFile = null;
    this.selectedImageFileName = '';
    this.clearSelectedImagePreview();

    this.authorControl.setValue(null, {emitEvent: false});
    this.blogCategoryControl.setValue(null, {emitEvent: false});
    this.insuranceCategoryControl.setValue(null, {emitEvent: false});
    this.relatedFacilityControl.setValue(null, {emitEvent: false});
    this.relatedBlogArticleControl.setValue(
      [],
      {emitEvent: false}
    );
    this.blogTagsControl.setValue(
      [],
      {emitEvent: false}
    );
    form.resetForm(this.model);

    this.articleReactiveForm.reset({
      description: '',
    });

  }

  private emptyToNull(value: any): string | null {
    return value === undefined || value === null || value === '' ? null : value;
  }

  private toDateTimeLocal(value?: string): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return value.slice(0, 16);
    }

    const pad = (number: number): string => number.toString().padStart(2, '0');

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  getCurrentArticleImage(): string | null {
    if (!this.blogArticle) {
      return null;
    }

    if (this.blogArticle.imagePath) {
      return this.buildPublicImageUrl(this.blogArticle.imagePath);
    }

    return this.buildPublicImageUrl(
      this.getImageVariantPath(this.blogArticle.imagesVariants, 'large')
      ?? this.getImageVariantPath(this.blogArticle.imagesVariants, 'medium')
      ?? this.getImageVariantPath(this.blogArticle.imagesVariants, 'original')
      ?? this.getImageVariantPath(this.blogArticle.imagesVariants, 'small')
    );
  }

  private getImageVariantPath(variants: any[] | undefined, size: string): string | null {
    if (!variants?.length) {
      return null;
    }

    const variant = variants.find(item => item.size === size);

    return variant?.path ?? null;
  }

  private buildPublicImageUrl(path?: string | null): string | null {
    if (!path) {
      return null;
    }

    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }

    if (path.startsWith('/public/')) {
      return `${environment.dobryAgentUrl}${path}`;
    }

    if (path.startsWith('/')) {
      return `${environment.dobryAgentUrl}/public${path}`;
    }

    return `${environment.dobryAgentUrl}/public/${path}`;
  }
}
